/**
 * auren-drag-slider: the homepage hero's lamp-vs-Serene comparison. The round
 * handle is a role="slider". Mouse and pen drag anywhere; a touch drags from the
 * handle at once, or from anywhere on the photo once it moves 8px more sideways
 * than up or down (touch-action is pan-y, so vertical swipes still scroll). A
 * finger carries the line under it, and within 14px of a side it goes all the
 * way. A tap or click glides there; arrows 5%, Page Up/Down 10%, Home/End to the
 * ends; one nudge on first view, never under reduced motion. Pure helpers are
 * exported for tests. No libraries.
 */

export const STEP = 5;
export const PAGE_STEP = 10;
export const TAP_SLOP_PX = 8;
export const END_SNAP_PX = 14;
export const TAP_MAX_MS = 600;
export const NUDGE_MS = 1400;
export const NUDGE_DELAY_MS = 450;
export const JUMP_MS = 220;

/** [progress 0-1, offset in points from where the nudge began] */
export const NUDGE_KEYFRAMES = [
  [0, 0],
  [0.35, 12],
  [0.75, -8],
  [1, 0],
];

/** 0 is all "Serene Light", 100 all "Bedside lamp". */
export function clampPercent(value, fallback = 50) {
  const number = Number(value);
  if (value === null || value === '' || !Number.isFinite(number)) return fallback;
  return Math.min(100, Math.max(0, number));
}

/** The position under a pointer, as a percentage of the frame. */
export function percentFromPointer(clientX, left, width) {
  if (!(Number(width) > 0)) return 50;
  return clampPercent(((Number(clientX) - Number(left)) / Number(width)) * 100);
}

/** Close to a side counts as the side: a thumb rarely lands on the last pixel. */
export function snapToEnds(percent, width, snap = END_SNAP_PX) {
  const value = clampPercent(percent);
  if (!(Number(width) > 0)) return value;
  const px = (value / 100) * Number(width);
  if (px <= snap) return 0;
  if (px >= Number(width) - snap) return 100;
  return value;
}

/** A move that is mostly sideways and past the slop: a drag, not a scroll. */
export function isSidewaysMove(start, end, slop = TAP_SLOP_PX) {
  if (!start || !end) return false;
  const dx = Math.abs(Number(end.x) - Number(start.x));
  return dx >= slop && dx > Math.abs(Number(end.y) - Number(start.y));
}

/** The new position for a key, or null when it is not the slider's. */
export function valueForKey(key, value, { step = STEP, pageStep = PAGE_STEP } = {}) {
  const current = clampPercent(value);
  switch (key) {
    case 'ArrowLeft':
    case 'ArrowDown':
      return clampPercent(current - step);
    case 'ArrowRight':
    case 'ArrowUp':
      return clampPercent(current + step);
    case 'PageDown':
      return clampPercent(current - pageStep);
    case 'PageUp':
      return clampPercent(current + pageStep);
    case 'Home':
      return 0;
    case 'End':
      return 100;
    default:
      return null;
  }
}

export function easeInOutCubic(t) {
  const x = Math.min(1, Math.max(0, t));
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

export function easeOutCubic(t) {
  const x = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - x, 3);
}

/** Ease right, then left, then home: ends where it began. */
export function nudgeValueAt(progress, start = 50, keyframes = NUDGE_KEYFRAMES) {
  const t = Math.min(1, Math.max(0, Number(progress) || 0));
  const base = clampPercent(start);
  for (let i = 1; i < keyframes.length; i += 1) {
    const [t0, v0] = keyframes[i - 1];
    const [t1, v1] = keyframes[i];
    if (t <= t1) {
      const local = t1 === t0 ? 1 : (t - t0) / (t1 - t0);
      return clampPercent(base + v0 + (v1 - v0) * easeInOutCubic(local));
    }
  }
  return clampPercent(base + keyframes[keyframes.length - 1][1]);
}

/** "62% Bedside lamp, 38% Serene Light" for aria-valuetext. */
export function valueText(value, before = 'Bedside lamp', after = 'Serene Light') {
  const shown = Math.round(clampPercent(value));
  return `${shown}% ${before}, ${100 - shown}% ${after}`;
}

/** A press that barely moved. */
export function isTap(start, end, slop = TAP_SLOP_PX) {
  if (!start || !end) return false;
  return Math.hypot(Number(end.x) - Number(start.x), Number(end.y) - Number(start.y)) <= slop;
}

if (typeof window !== 'undefined' && 'customElements' in window && !window.customElements.get('auren-drag-slider')) {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AurenDragSlider extends HTMLElement {
    constructor() {
      super();
      this.value = 50;
      this.pointer = null;
      this.interacted = false;
      this.nudged = false;
      ['onPointerDown', 'onPointerMove', 'onPointerUp', 'onPointerCancel', 'onKeyDown', 'onResize'].forEach((name) => {
        this[name] = this[name].bind(this);
      });
    }

    connectedCallback() {
      this.frame = this.querySelector('[data-au-drag-frame]');
      this.handle = this.querySelector('[data-au-drag-handle]');
      if (!this.frame || !this.handle) return;
      this.labelBefore = this.dataset.labelBefore || 'Bedside lamp';
      this.labelAfter = this.dataset.labelAfter || 'Serene Light';
      this.value = clampPercent(parseFloat(this.style.getPropertyValue('--pos')));

      if (!this.bound) {
        const f = this.frame;
        f.addEventListener('pointerdown', this.onPointerDown);
        f.addEventListener('pointermove', this.onPointerMove);
        f.addEventListener('pointerup', this.onPointerUp);
        f.addEventListener('pointercancel', this.onPointerCancel);
        f.addEventListener('lostpointercapture', this.onPointerCancel);
        f.addEventListener('dragstart', (event) => event.preventDefault());
        this.handle.addEventListener('keydown', this.onKeyDown);
        this.bound = true;
      }
      window.addEventListener('resize', this.onResize);
      this.render();
      // On the next frame, not now: reading the layout during start-up forced
      // a full layout of the page before the browser was ready to do one.
      this.fitFrame = window.requestAnimationFrame(() => this.fit());
      this.watchForFirstView();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      this.observer?.disconnect();
      this.observer = null;
      this.stopNudge();
      window.cancelAnimationFrame(this.jumpFrame);
      window.cancelAnimationFrame(this.fitFrame);
    }

    /** The announcement bar and header above the hero set its height. */
    fit() {
      const hero = this.closest('.au-hs');
      if (!hero) return;
      const top = Math.round(hero.getBoundingClientRect().top + window.scrollY);
      if (top > 0) hero.style.setProperty('--hs-offset', `${top}px`);
    }

    onResize() {
      window.cancelAnimationFrame(this.fitFrame);
      this.fitFrame = window.requestAnimationFrame(() => this.fit());
    }

    setValue(value) {
      this.value = clampPercent(value, this.value);
      this.render();
    }

    render() {
      const shown = Math.round(this.value * 10) / 10;
      this.style.setProperty('--pos', `${shown}%`);
      this.style.setProperty('--p', String(shown));
      const now = String(Math.round(this.value));
      if (this.handle.getAttribute('aria-valuenow') !== now) {
        this.handle.setAttribute('aria-valuenow', now);
        this.handle.setAttribute('aria-valuetext', valueText(this.value, this.labelBefore, this.labelAfter));
      }
    }

    markInteracted() {
      this.interacted = true;
      this.stopNudge();
      window.cancelAnimationFrame(this.jumpFrame);
    }

    onKeyDown(event) {
      const next = valueForKey(event.key, this.value);
      if (next === null) return;
      event.preventDefault();
      this.markInteracted();
      this.setValue(next);
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (this.pointer) return;
      this.markInteracted();
      const rect = this.frame.getBoundingClientRect();
      this.pointer = {
        id: event.pointerId,
        type: event.pointerType,
        x: event.clientX,
        y: event.clientY,
        time: event.timeStamp,
        left: rect.left,
        width: rect.width,
        onHandle: this.handle.contains(event.target),
        grab: 0,
        dragging: false,
      };
      // A mouse grabbing the badge off-centre must not make the line jump. A
      // finger has no such grip: the line comes to it, so both sides are in reach.
      if (this.pointer.onHandle && event.pointerType !== 'touch') {
        this.pointer.grab = event.clientX - (rect.left + (this.value / 100) * rect.width);
      }
      // Mouse and pen drag at once; a touch only from the handle.
      if (event.pointerType !== 'touch' || this.pointer.onHandle) this.beginDrag(event);
      if (event.pointerType === 'mouse') event.preventDefault();
    }

    beginDrag(event) {
      const pointer = this.pointer;
      pointer.dragging = true;
      this.classList.add('is-dragging');
      try {
        this.frame.setPointerCapture(pointer.id);
      } catch {
        /* the pointer is already gone */
      }
      if (pointer.type !== 'touch' && !pointer.onHandle) {
        this.jumpTo(percentFromPointer(event.clientX, pointer.left, pointer.width));
      }
      if (document.activeElement !== this.handle) this.handle.focus({ preventScroll: true });
    }

    onPointerMove(event) {
      const pointer = this.pointer;
      if (!pointer || event.pointerId !== pointer.id) return;
      if (!pointer.dragging) {
        if (!isSidewaysMove(pointer, { x: event.clientX, y: event.clientY })) return;
        this.beginDrag(event);
      }
      if (!(pointer.width > 0)) return;
      window.cancelAnimationFrame(this.jumpFrame);
      const percent = percentFromPointer(event.clientX - pointer.grab, pointer.left, pointer.width);
      this.setValue(snapToEnds(percent, pointer.width));
    }

    onPointerUp(event) {
      const pointer = this.pointer;
      if (!pointer || event.pointerId !== pointer.id) return;
      const tapped =
        !pointer.dragging &&
        event.timeStamp - pointer.time <= TAP_MAX_MS &&
        isTap(pointer, { x: event.clientX, y: event.clientY });
      if (tapped) this.jumpTo(percentFromPointer(event.clientX, pointer.left, pointer.width));
      this.endPointer();
    }

    onPointerCancel(event) {
      if (this.pointer && event.pointerId === this.pointer.id) this.endPointer();
    }

    endPointer() {
      const pointer = this.pointer;
      this.pointer = null;
      this.classList.remove('is-dragging');
      if (pointer && pointer.dragging && this.frame.hasPointerCapture?.(pointer.id)) {
        try {
          this.frame.releasePointerCapture(pointer.id);
        } catch {
          /* already released */
        }
      }
    }

    /** A short glide, or at once under reduced motion. */
    jumpTo(target) {
      window.cancelAnimationFrame(this.jumpFrame);
      if (still.matches) {
        this.setValue(target);
        return;
      }
      const from = this.value;
      const startedAt = window.performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - startedAt) / JUMP_MS);
        this.setValue(from + (target - from) * easeOutCubic(t));
        if (t < 1) this.jumpFrame = window.requestAnimationFrame(step);
      };
      this.jumpFrame = window.requestAnimationFrame(step);
    }

    watchForFirstView() {
      if (this.nudged || this.interacted || this.observer || still.matches) return;
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer?.disconnect();
          this.observer = null;
          this.nudgeTimer = window.setTimeout(() => this.nudge(), NUDGE_DELAY_MS);
        },
        { threshold: 0.35 }
      );
      this.observer.observe(this);
    }

    nudge() {
      if (this.nudged || this.interacted || still.matches || document.hidden) return;
      this.nudged = true;
      const start = this.value;
      const startedAt = window.performance.now();
      const step = (now) => {
        if (this.interacted) return;
        const t = (now - startedAt) / NUDGE_MS;
        this.setValue(nudgeValueAt(t, start));
        if (t < 1) this.nudgeFrame = window.requestAnimationFrame(step);
      };
      this.nudgeFrame = window.requestAnimationFrame(step);
    }

    stopNudge() {
      window.clearTimeout(this.nudgeTimer);
      window.cancelAnimationFrame(this.nudgeFrame);
    }
  }

  window.customElements.define('auren-drag-slider', AurenDragSlider);

  // The hero button scrolls to the buy box when the page has one, else it follows its link.
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-au-jump]');
    if (!link || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = document.getElementById(link.dataset.auJump);
    if (!target) return;
    event.preventDefault();
    const header = document.querySelector('.section-header');
    const top = target.getBoundingClientRect().top + window.scrollY - (header ? header.offsetHeight : 0) - 12;
    window.scrollTo({ top, behavior: still.matches ? 'auto' : 'smooth' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });
}
